// src/features/medicine-inventory/pages/MedicinePurchase.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import { useInventory } from '../hooks/useInventory';
import toast from 'react-hot-toast';

const MedicinePurchase = () => {
    const navigate = useNavigate();
    const { medicines, multiPurchaseStock } = useInventory();

    const [formData, setFormData] = useState({
        supplier_name: '',
        invoice_number: '',
        purchase_date: new Date().toISOString().split('T')[0],
        medicines: []
    });

    const [submitting, setSubmitting] = useState(false);

    // Add medicine row
    const handleAddMedicine = () => {
        setFormData(prev => ({
            ...prev,
            medicines: [
                ...prev.medicines,
                {
                    medicine_id: '',
                    batch_number: '',
                    expiry_date: '',
                    quantity: 1,
                    unit_price: ''
                }
            ]
        }));
    };

    // Remove row
    const handleRemoveMedicine = (index) => {
        setFormData(prev => ({
            ...prev,
            medicines: prev.medicines.filter((_, i) => i !== index)
        }));
    };

    // Handle change
    const handleChange = (index, field, value) => {
        const updated = [...formData.medicines];

        if (field === 'medicine_id') {
            const selected = medicines.find(m => m.id == value);

            updated[index] = {
                ...updated[index],
                medicine_id: value,
                unit_price: selected?.price || ''
            };
        } else {
            updated[index][field] = value;
        }

        setFormData(prev => ({
            ...prev,
            medicines: updated
        }));
    };

    // Validate
    const validate = () => {
        if (!formData.purchase_date) {
            toast.error('Purchase date required');
            return false;
        }

        if (formData.medicines.length === 0) {
            toast.error('Add at least one medicine');
            return false;
        }

        for (const item of formData.medicines) {
            if (!item.medicine_id || !item.batch_number || !item.expiry_date) {
                toast.error('All medicine fields are required');
                return false;
            }
        }

        return true;
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setSubmitting(true);

        try {
            const payload = {
                supplier_name: formData.supplier_name,
                invoice_number: formData.invoice_number,
                purchase_date: formData.purchase_date,
                medicines: formData.medicines.map(m => ({
                    medicine_id: parseInt(m.medicine_id),
                    batch_number: m.batch_number,
                    expiry_date: m.expiry_date,
                    quantity: parseFloat(m.quantity),
                    unit_price: parseFloat(m.unit_price)
                }))
            };

            await multiPurchaseStock(payload);

            toast.success('Stock purchased successfully');
            navigate('/stock');
        } catch (err) {
            console.error(err);
            toast.error('Purchase failed');
        } finally {
            setSubmitting(false);
        }
    };

    // Total calculation
    const calculateTotal = () => {
        return formData.medicines.reduce((sum, m) => {
            return sum + (m.quantity * m.unit_price || 0);
        }, 0);
    };

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center">
                <button
                    onClick={() => navigate('/stock')}
                    className="mr-4 text-gray-600 hover:text-gray-900"
                >
                    <FaArrowLeft />
                </button>
                <h1 className="text-2xl font-bold">Medicine Purchase</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Supplier Info */}
                <div className="bg-white p-6 rounded shadow">
                    <h2 className="font-semibold mb-4">Purchase Details</h2>

                    <div className="grid grid-cols-3 gap-4">
                        <input
                            type="text"
                            placeholder="Supplier Name"
                            className="border p-2 rounded"
                            value={formData.supplier_name}
                            onChange={e => setFormData({ ...formData, supplier_name: e.target.value })}
                        />

                        <input
                            type="text"
                            placeholder="Invoice Number"
                            className="border p-2 rounded"
                            value={formData.invoice_number}
                            onChange={e => setFormData({ ...formData, invoice_number: e.target.value })}
                        />

                        <input
                            type="date"
                            className="border p-2 rounded"
                            value={formData.purchase_date}
                            onChange={e => setFormData({ ...formData, purchase_date: e.target.value })}
                            required
                        />
                    </div>
                </div>

                {/* Medicines */}
                <div className="bg-white p-6 rounded shadow">
                    <div className="flex justify-between mb-4">
                        <h2 className="font-semibold">Medicines</h2>
                        <button type="button" onClick={handleAddMedicine} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 text-sm rounded">
                            <FaPlus /> Add
                        </button>
                    </div>
                    <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_auto] gap-3 font-semibold text-sm text-gray-700 border-b pb-2 mb-2">
                        <div>Medicine</div>
                        <div>Batch</div>
                        <div>Expiry</div>
                        <div>Qty</div>
                        <div className="text-center"> </div>
                    </div>

                    {formData.medicines.map((item, index) => (
                        <div key={index} className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_auto] gap-3 mb-3 border p-3 rounded items-center">

                            {/* Medicine */}
                            <select
                                className="border p-2 rounded text-sm"
                                value={item.medicine_id}
                                onChange={e => handleChange(index, 'medicine_id', e.target.value)}
                            >
                                <option value="">Select</option>
                                {medicines.map(m => (
                                    <option key={m.id} value={m.id}>
                                        {m.name} ({m.strength})
                                    </option>
                                ))}
                            </select>

                            {/* Batch */}
                            <input
                                type="text"
                                placeholder="Batch"
                                className="border p-2 rounded text-sm"
                                value={item.batch_number}
                                onChange={e => handleChange(index, 'batch_number', e.target.value)}
                            />

                            {/* Expiry */}
                            <input
                                type="date"
                                className="border p-2 rounded text-sm"
                                value={item.expiry_date}
                                onChange={e => handleChange(index, 'expiry_date', e.target.value)}
                            />

                            {/* Quantity */}
                            <input
                                type="number"
                                min="1"
                                className="border p-2 rounded text-sm w-20"
                                value={item.quantity}
                                onChange={e => handleChange(index, 'quantity', e.target.value)}
                            />

                            {/* Action (MINIMAL SPACE) */}
                            <button
                                type="button"
                                onClick={() => handleRemoveMedicine(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                                title="Remove"
                            >
                                <FaTrash size={14} />
                            </button>

                        </div>
                    ))}

                    {/* Total */}
                    {/* <div className="text-right font-bold mt-4">
                        Total: ₹ {calculateTotal().toFixed(2)}
                    </div> */}
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/stock')}
                        className="border px-4 py-2 rounded"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-blue-600 text-white px-4 py-2 rounded"
                    >
                        {submitting ? 'Processing...' : 'Purchase'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MedicinePurchase;